import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, MapPinned, Loader2 } from 'lucide-react';

const CLASS_COLORS = {
  // ESA WorldCover v200 palette (aligned with ESA's official style)
  tree_cover: 'bg-emerald-100 text-emerald-800',
  shrubland: 'bg-yellow-100 text-yellow-800',
  grassland: 'bg-lime-100 text-lime-800',
  cropland: 'bg-amber-100 text-amber-800',
  built_up: 'bg-rose-100 text-rose-800',
  bare: 'bg-stone-100 text-stone-800',
  snow_and_ice: 'bg-slate-100 text-slate-800',
  permanent_water: 'bg-sky-100 text-sky-800',
  herbaceous_wetland: 'bg-teal-100 text-teal-800',
  mangroves: 'bg-emerald-200 text-emerald-900',
  moss_and_lichen: 'bg-neutral-100 text-neutral-800',
};

const LandCoverGate = ({ loading, result }) => {
  if (loading) {
    return (
      <div
        data-testid="landcover-loading"
        className="rounded-xl border border-icrisat-green/30 bg-icrisat-green-soft/60 p-4 flex items-center gap-3 text-icrisat-green"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Classifying land cover with ESA WorldCover (10 m)…</span>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="rounded-xl border border-dashed border-icrisat-green/30 bg-white/50 p-4 text-sm text-muted-foreground flex items-center gap-2">
        <MapPinned className="h-4 w-4" />
        Draw a polygon around your field on the satellite map above, then tap
        <b className="mx-1">Check land cover</b>
        to confirm it's a cultivated parcel.
      </div>
    );
  }

  const proceed = result.decision === 'PROCEED';
  return (
    <div
      data-testid="landcover-result"
      className={`rounded-xl border p-4 space-y-3 ${
        proceed ? 'border-emerald-300 bg-emerald-50' : 'border-rose-300 bg-rose-50'
      }`}
    >
      <div className="flex items-start gap-3">
        {proceed ? (
          <CheckCircle2 className="h-6 w-6 text-emerald-700 shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="h-6 w-6 text-rose-700 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <div className={`font-display text-lg ${proceed ? 'text-emerald-900' : 'text-rose-900'}`}>
            {proceed ? 'Cropland parcel confirmed' : 'Not a cropped area'}
          </div>
          <p className={`text-sm mt-1 ${proceed ? 'text-emerald-900/90' : 'text-rose-900/90'}`}>
            {result.message}
          </p>
          <p className="text-[11px] mt-1 text-muted-foreground italic">
            Gate rule · ESA WorldCover v200 · cropland must be ≥ {result.min_crop_area_pct ?? 80}% of parcel
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <Stat label="Parcel area" value={`${result.parcel.area_ha} ha`} />
        <Stat label="Dominant" value={result.dominant_label || result.dominant} />
        <Stat label="Crop area" value={`${result.crop_area_pct}%`} />
        <Stat label="Data source" value={result.source} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {result.top_classes.map((c) => (
          <Badge key={c.class} className={CLASS_COLORS[c.class] || 'bg-slate-100 text-slate-700'}>
            {c.class.replace(/_/g, ' ')} · {c.area_pct}%
          </Badge>
        ))}
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-md bg-white/70 px-2 py-1.5 border border-black/5">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-medium text-icrisat-green truncate">{value}</div>
  </div>
);

export default LandCoverGate;
